/**
 * The home page. One function per section, mirroring the sections of the design
 * source. Navigation and the footer are shared with every page — see chrome.js.
 */

import { h } from '../lib/dom.js';
import { CONTENT, OFFICE } from '../content.js';
import { COMPANY } from '../company.js';
import { LEGAL } from '../legal.js';
import { routeLink } from './chrome.js';
import { renderForm } from './form.js';

// Served straight from public/ so index.html's social-preview tags and the
// deploy script's per-path cache headers can both name a stable URL.
const portrait = '/assets/portrait.webp';

export function renderPage(lang) {
  const c = CONTENT[lang];
  return [
    hero(c),
    h('hr', { class: 'hr', style: 'margin: 0' }),
    territories(c),
    h('hr', { class: 'hr', style: 'margin: 0' }),
    services(c),
    about(c),
    h('hr', { class: 'hr', style: 'margin: 0' }),
    engage(lang, c),
    h('hr', { class: 'hr', style: 'margin: 0' }),
    contact(c),
    write(lang, c),
  ];
}


function hero(c) {
  return h(
    'section',
    { class: 'hero' },
    h('h1', { class: 'hero-title' }, c.hero.lines.map((line) => h('span', null, line))),
    h('p', { class: 'hero-lead' }, c.hero.lead),
    h(
      'div',
      { class: 'actions' },
      h('a', { class: 'btn btn-primary', href: '#services' }, c.hero.primary),
      h('a', { class: 'btn btn-ghost', href: '#contact' }, c.hero.ghost),
    ),
  );
}

function territories(c) {
  return h(
    'section',
    { class: 'territories' },
    c.territories.places.map((place) => h('span', null, place)),
    h('span', { class: 'years' }, c.territories.years),
  );
}

function services(c) {
  return h(
    'section',
    { class: 'services', id: 'services' },
    h('span', { class: 'kicker' }, c.services.kicker),
    h('h2', { class: 'services-title' }, c.services.title),
    h(
      'div',
      { class: 'services-grid' },
      c.services.items.map((item, i) =>
        h(
          'div',
          { class: 'service' },
          h('span', { class: 'service-num', 'aria-hidden': 'true' }, String(i + 1).padStart(2, '0')),
          h('h3', null, item.title),
          h('p', null, item.body),
        ),
      ),
    ),
  );
}

/**
 * How an engagement is priced and paid for. Anyone deciding whether to write to
 * us wants this before they do; a card acquirer reviewing the site requires it
 * to be here at all.
 */
function engage(lang, c) {
  const e = c.engage;
  const l = LEGAL[lang];
  return h(
    'section',
    { class: 'engage', id: 'fees' },
    h('span', { class: 'kicker' }, e.kicker),
    h('h2', { class: 'engage-title' }, e.title),
    h('p', { class: 'engage-lead' }, e.lead),
    h(
      'div',
      { class: 'engage-grid' },
      e.items.map(([title, body]) =>
        h('div', { class: 'engage-item' }, h('h3', null, title), h('p', null, body)),
      ),
    ),
    h(
      'p',
      { class: 'engage-note' },
      e.note,
      ' ',
      ...['terms', 'refunds'].flatMap((page, i) => [i ? ' · ' : '', routeLink(lang, page, l.nav[page])]),
    ),
  );
}

function about(c) {
  return h(
    'section',
    { class: 'about', id: 'about' },
    h(
      'div',
      null,
      h('span', { class: 'kicker' }, c.about.kicker),
      h('h2', { class: 'about-title' }, c.about.title),
      h('p', null, c.about.body),
    ),
    h(
      'figure',
      { class: 'plate portrait' },
      h('img', { src: portrait, alt: c.about.portraitAlt, width: 445, height: 556, loading: 'lazy', decoding: 'async' }),
    ),
  );
}

function contact(c) {
  const block = (label, ...body) =>
    h('div', null, h('span', { class: 'contact-label' }, label), ...body);

  return h(
    'section',
    { class: 'contact', id: 'contact' },
    h('span', { class: 'kicker' }, c.contact.kicker),
    h('h2', { class: 'contact-title' }, COMPANY.legalName),
    h(
      'div',
      { class: 'contact-grid' },
      block(
        c.contact.phone,
        h(
          'p',
          { class: 'tnum' },
          OFFICE.phones.flatMap((line, i) => (i ? [h('br'), line] : [line])),
        ),
      ),
      block(c.contact.email, h('p', null, h('a', { href: `mailto:${COMPANY.email}` }, COMPANY.email))),
      block(c.contact.hk, h('p', null, COMPANY.registeredAddress)),
      block(c.contact.macau, h('p', { class: 'tnum' }, OFFICE.macauAddress)),
      block(c.contact.web, h('p', null, h('a', { href: OFFICE.web.href }, OFFICE.web.label))),
    ),
    // One merchant, one jurisdiction: the Macau and mainland details are ways to
    // reach us, not places a client contracts with or pays.
    h('p', { class: 'contact-note' }, c.contact.contracting),
  );
}

function write(lang, c) {
  return h(
    'section',
    { class: 'write' },
    h('div', null, h('h3', { class: 'write-title' }, c.write.title), h('p', { class: 'write-body' }, c.write.body)),
    renderForm(lang),
  );
}
